/**
 * Guarded access to `window.localStorage`.
 *
 * Reading the `localStorage` *property* — not just its methods — can throw on
 * iOS. Outlook and the Microsoft 365 app render tabs in a WKWebView, and the tab
 * lives in a cross-origin frame, so WebKit's cross-site tracking prevention
 * treats our storage as third-party. When it is blocked, `window.localStorage`
 * throws `SecurityError: The operation is insecure` on the very first access.
 * Android's Chromium WebView allows third-party storage by default, which is why
 * an unguarded `localStorage.getItem(...)` works there and takes the app down on
 * iOS only.
 *
 * A throw in a React render (a `useState` initializer, say) unwinds the whole
 * tree to the nearest error boundary before any effect commits — including the
 * effect that dismisses the M365 host's loading indicator. The host then sits
 * behind its shimmer forever and the app looks like it never loaded.
 *
 * Preferences that can be read during render or app bootstrap should go through
 * here. When persistent storage is unavailable we fall back to an in-memory map:
 * preferences stop surviving a reload, which is the correct degradation for a
 * privacy-partitioned webview, and nothing throws.
 */

/** Fallback store used when `localStorage` is unreachable. Session-scoped. */
const memoryStore = new Map<string, string>();

/**
 * Resolved lazily and cached: the answer cannot change within a document, and we
 * do not want to pay a throw/catch on every read.
 */
let persistentStorage: Storage | null | undefined;

function resolveStorage(): Storage | null {
  if (persistentStorage !== undefined) {
    return persistentStorage;
  }

  try {
    const storage = globalThis.localStorage;
    if (!storage) {
      persistentStorage = null;
      return null;
    }
    // Safari in "block all cookies" mode exposes the object but throws on write,
    // so probe with a real round-trip rather than trusting its presence.
    const probeKey = '__qd_storage_probe__';
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    persistentStorage = storage;
  } catch {
    persistentStorage = null;
  }

  return persistentStorage;
}

/** Whether preferences written here will survive a reload. */
export function isPersistentStorageAvailable(): boolean {
  return resolveStorage() !== null;
}

export function readStoredValue(key: string): string | null {
  const storage = resolveStorage();
  if (!storage) {
    return memoryStore.get(key) ?? null;
  }
  try {
    return storage.getItem(key);
  } catch {
    return memoryStore.get(key) ?? null;
  }
}

export function writeStoredValue(key: string, value: string): void {
  memoryStore.set(key, value);
  const storage = resolveStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(key, value);
  } catch {
    // Quota exceeded or storage revoked mid-session. The memory copy above still
    // serves this session; a preference not persisting is never worth a throw.
  }
}

export function removeStoredValue(key: string): void {
  memoryStore.delete(key);
  const storage = resolveStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(key);
  } catch {
    // Nothing to recover — the value is already gone from the memory store.
  }
}

/** Test seam: forget the cached probe result so a test can change the environment. */
export function resetStorageProbeForTests(): void {
  persistentStorage = undefined;
  memoryStore.clear();
}
